import React from 'react';
import { useTranslation } from 'react-i18next';
import { BodyPortal } from '@/components/BodyPortal';
import { Button } from '@/components/Button';
import { CircleClock } from '@/components/Icons/CircleClock';
import { WebcamVideo } from '@/components/Icons/WebcamVideo';
import { LessonVideoChip } from '@/components/LessonVideoChip';
import { useLessonVideoMeeting } from '@/components/LessonVideoMeeting/hooks/useLessonVideoMeeting';
import { Snackbar } from '@/components/Snackbar';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  BorderRadius,
  ButtonVariant,
  Size,
  SnackbarVariant,
} from '@/types/components';
import type { VideoMeetingResponse } from '@/types/video';

interface Props {
  meeting: VideoMeetingResponse;
}

export const LessonVideoMeeting: React.FC<Props> = ({ meeting: response }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const {
    meeting,
    isJoinVisible,
    isErrorSnackbarOpen,
    join,
    closeErrorSnackbar,
  } = useLessonVideoMeeting(response);

  return (
    <div
      className={`flex flex-col rounded-xl bg-card p-3 shadow-card contrast:border contrast:border-border`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <LessonVideoChip />
        <span className="flex items-center gap-1 text-body-13 text-subtle-foreground md:text-body-14">
          <CircleClock className="size-4 shrink-0" />
          {meeting.time}
        </span>
      </div>

      {!!meeting.title && (
        <p className="mt-2 text-subhead-14 text-foreground">{meeting.title}</p>
      )}

      {isJoinVisible && (
        <Button
          variant={ButtonVariant.Primary}
          size={isMobile ? Size.Large : Size.Small}
          radius={BorderRadius.Pill}
          className="mt-3 self-start max-md:w-full"
          onClick={join}
        >
          <WebcamVideo className="size-4 shrink-0 max-md:size-6" />
          {t('mail.video.join')}
        </Button>
      )}

      <BodyPortal>
        <Snackbar
          isOpen={isErrorSnackbarOpen}
          variant={SnackbarVariant.Error}
          message={t('global.errorOccurred')}
          onDismiss={closeErrorSnackbar}
        />
      </BodyPortal>
    </div>
  );
};
